import { forwardRef, useId, type InputHTMLAttributes, type SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

interface BaseFieldProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export type TextFieldProps = BaseFieldProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>;

/**
 * Text input with an associated <label> and aria-describedby error/hint wiring
 * (spec §16). Forwards the ref so react-hook-form can register it.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, className, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        ref={ref}
        className={clsx('field__input', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} aria-live="polite">
          {error}
        </span>
      ) : null}
    </div>
  );
});

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectFieldProps = BaseFieldProps & {
  options: SelectOption[];
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'>;

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, options, className, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        ref={ref}
        className={clsx('field__select', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} aria-live="polite">
          {error}
        </span>
      ) : null}
    </div>
  );
});

export type CheckboxFieldProps = {
  label: string;
  error?: string | undefined;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'>;

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  function CheckboxField({ label, error, ...rest }, ref) {
    const id = useId();
    const errorId = `${id}-error`;
    return (
      <div className="checkbox-field">
        <input
          id={id}
          ref={ref}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...rest}
        />
        <label htmlFor={id}>
          {label}
          {error ? (
            <span className="field__error" id={errorId} aria-live="polite">
              {' '}
              {error}
            </span>
          ) : null}
        </label>
      </div>
    );
  },
);
